import React, { useState } from 'react';
import { useSurvey } from '../providers/SurveyProvider';
import { toast } from 'react-toastify';

interface Question {
  id: number;
  question: string;
  options: string;
}

interface SurveyData {
  id: number;
  name: string;
  message: string;
  duration: string;
  questions: Question[];
}

const SurveyPopup: React.FC = () => {
  const {
    showSurvey,
    setShowSurvey,
    surveyData,
    isLoading,
    submitSurvey,
    showSuccessMessage,
    setShowSuccessMessage,
  } = useSurvey();
  const [responses, setResponses] = useState<Record<number, string>>({});
  const [otherValues, setOtherValues] = useState<Record<number, string>>({});

  // Success message popup
  if (showSuccessMessage && surveyData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
            <div className="text-gray-700 mb-6 whitespace-pre-line">
              {surveyData.message}
            </div>
            <button
              onClick={() => setShowSuccessMessage(false)}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!showSurvey || isLoading || !surveyData) {
    return null;
  }

  const handleOptionChange = (questionId: number, value: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleOtherValueChange = (questionId: number, value: string) => {
    setOtherValues((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const allQuestionsAnswered = (surveyData as SurveyData).questions.every(
      (question: Question) =>
        responses[question.id] || otherValues[question.id],
    );

    if (!allQuestionsAnswered) {
      toast.error('Please answer all questions before submitting.');
      return;
    }

    // Combine responses with other values
    const finalResponses = { ...responses };
    Object.entries(otherValues).forEach(([questionId, value]) => {
      if (value) {
        finalResponses[Number(questionId)] = value;
      }
    });

    await submitSurvey(finalResponses);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            You've been coding with us for {surveyData?.name || 3} months, we'd
            love your feedback!
          </h2>
          <button
            onClick={() => setShowSurvey(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Help us make My Code Dragon even better by answering{' '}
          {surveyData?.questions?.length || ''} super quick multiple choice
          questions!
        </p>

        <div className="space-y-6">
          {(surveyData as SurveyData).questions.map((question: Question) => (
            <div key={question.id} className="border-b pb-4">
              <p className="font-medium mb-3">{question.question}</p>
              <div className="space-y-2">
                {question.options === '.' ? (
                  <textarea
                    value={otherValues[question.id] || ''}
                    onChange={(e) =>
                      handleOtherValueChange(question.id, e.target.value)
                    }
                    placeholder="Enter your answer here..."
                    className="w-full p-2 border rounded mt-2"
                    rows={2}
                  />
                ) : (
                  question.options
                    .split(',')
                    .map((option: string, index: number) => (
                      <div key={index} className="flex items-center">
                        <input
                          type="radio"
                          id={`q${question.id}-o${index}`}
                          name={`question-${question.id}`}
                          value={option.trim()}
                          checked={responses[question.id] === option.trim()}
                          onChange={(e) =>
                            handleOptionChange(question.id, e.target.value)
                          }
                          className="mr-2"
                        />
                        <label htmlFor={`q${question.id}-o${index}`}>
                          {option.trim()}
                        </label>
                      </div>
                    ))
                )}
                {responses[question.id]?.includes('Other') && (
                  <textarea
                    value={otherValues[question.id] || ''}
                    onChange={(e) =>
                      handleOtherValueChange(question.id, e.target.value)
                    }
                    placeholder="Please specify..."
                    className="w-full p-2 border rounded mt-2"
                    rows={2}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSubmit}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Submit Survey
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyPopup;
